'use client';

import { useTranslations } from 'next-intl';
import { ArrowLeft, Loader2, Save } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PageActionBar } from '@/components/layout/page-action-bar';
import type { Driver } from '@/features/drivers';

const AVATAR_COLORS = [
  'bg-blue-500',
  'bg-emerald-500',
  'bg-violet-500',
  'bg-amber-500',
  'bg-rose-500',
  'bg-cyan-500',
  'bg-indigo-500',
  'bg-teal-500'
];

function getAvatarColor(id: number): string {
  return AVATAR_COLORS[id % AVATAR_COLORS.length];
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

interface ShippingAssignFooterProps {
  selectedBoxCount: number;
  totalWeight: number;
  selectedDriver: Driver | null;
  onSaveDraft: () => void;
  onConfirm: () => void;
  isConfirming: boolean;
  isDraftSaving: boolean;
}

export function ShippingAssignFooter({
  selectedBoxCount,
  totalWeight,
  selectedDriver,
  onSaveDraft,
  onConfirm,
  isConfirming,
  isDraftSaving
}: ShippingAssignFooterProps) {
  const t = useTranslations('shipping');

  const driverName = selectedDriver?.name;
  const canConfirm =
    selectedBoxCount > 0 &&
    selectedDriver != null &&
    selectedDriver.driver_profile != null;

  return (
    <PageActionBar
      start={
        <div className='flex flex-wrap items-center gap-x-6 gap-y-2'>
          <div className='flex items-center gap-2'>
            <span className='text-muted-foreground text-sm'>
              {t('selectedBoxes')}
            </span>
            <Badge variant='secondary'>
              {t('boxCountSummary', {
                count: selectedBoxCount,
                weight: totalWeight
              })}
            </Badge>
          </div>

          <div className='flex items-center gap-2'>
            <span className='text-muted-foreground text-sm'>
              {t('selectedDriver')}
            </span>
            {selectedDriver ? (
              <div className='flex items-center gap-2'>
                <div
                  className={cn(
                    'flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold text-white',
                    getAvatarColor(selectedDriver.id)
                  )}
                >
                  {getInitials(driverName ?? '')}
                </div>
                <span className='text-sm font-medium'>{driverName}</span>
              </div>
            ) : (
              <span className='text-muted-foreground text-sm italic'>
                {t('noDriverSelected')}
              </span>
            )}
          </div>
        </div>
      }
    >
      <Button variant='outline' onClick={onSaveDraft} disabled={isDraftSaving}>
        {isDraftSaving ? (
          <Loader2 className='me-2 h-4 w-4 animate-spin' />
        ) : (
          <Save className='me-2 h-4 w-4' />
        )}
        {t('saveDraft')}
      </Button>
      <Button onClick={onConfirm} disabled={!canConfirm || isConfirming}>
        {isConfirming ? (
          <Loader2 className='me-2 h-4 w-4 animate-spin' />
        ) : (
          <ArrowLeft className='me-2 h-4 w-4 rtl:rotate-180' />
        )}
        {t('confirmAssignment')}
      </Button>
    </PageActionBar>
  );
}
